/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from 'react';
import CustomOverLay from '../../../../components/CustomOverLay';
import GlobalContext from '../../../../components/Context/GloablContext';
import Emptyscreenwithouttitle from '../../../../components/Emptyscreenwithouttitle';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import formatMobileNumber from '../../../../CommonMethods/FormatMobileNumber';
import { 
    useGetAlertPreferencesQuery, 
    useUpdateCarePartnerAlertPreferencesMutation 
} from '../../../../features/patientSlice';

// Row component with internal optimistic state - prevents parent re-render
const CarePartnerRow = React.memo(({ 
    partner, 
    index, 
    updateCarePartnerAlertPreferences,
    setToastmessage,
    setToastErrorMessage,
    serverPartnerData
}) => {
    const [optimisticUpdates, setOptimisticUpdates] = useState({});
    const [updatingField, setUpdatingField] = useState(null);

    // Clear optimistic updates when server data matches
    useEffect(() => {
        if (serverPartnerData && Object.keys(optimisticUpdates).length > 0) {
            setOptimisticUpdates(prev => {
                const newState = { ...prev };
                let hasChanges = false;
                Object.keys(newState).forEach(key => {
                    const field = key.replace(`${partner.user_id}-`, '');
                    const serverValue = serverPartnerData[field] === true || serverPartnerData[field] === "true" || serverPartnerData[field] === 1;
                    const optimisticValue = newState[key];
                    if (serverValue === optimisticValue) {
                        delete newState[key];
                        hasChanges = true;
                    }
                });
                return hasChanges ? newState : prev;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverPartnerData, partner.user_id]);

    const handleToggle = async (field, value) => {
        const fieldKey = `${partner.user_id}-${field}`;
        setUpdatingField(fieldKey);
        
        // Optimistic update: immediately update local state
        setOptimisticUpdates(prev => ({
            ...prev,
            [fieldKey]: value
        }));

        try {
            const response = await updateCarePartnerAlertPreferences({ 
                carePartnerId: partner.user_id, 
                body: { [field]: value } 
            });
            if (response?.error?.data?.message) {
                // Rollback optimistic update on error
                setOptimisticUpdates(prev => {
                    const newState = { ...prev };
                    delete newState[fieldKey];
                    return newState;
                });
                setToastErrorMessage(response.error.data.message);
            } else {
                setToastmessage('Care partner alert preference updated successfully');
            }
        } catch (error) {
            // Rollback optimistic update on error
            setOptimisticUpdates(prev => {
                const newState = { ...prev };
                delete newState[fieldKey];
                return newState;
            });
            setToastErrorMessage('Failed to update care partner alert preference');
        } finally {
            setUpdatingField(null);
        }
    };

    // Helper to get toggle value (server data or optimistic update)
    const getToggleValue = (field) => {
        const optimisticKey = `${partner.user_id}-${field}`;
        if (optimisticUpdates.hasOwnProperty(optimisticKey)) {
            return optimisticUpdates[optimisticKey];
        }
        return partner[field] === true || partner[field] === "true" || partner[field] === 1;
    };

    return (
        <tr className="hover:bg-[#F8EEF1] h-[45px]">
            <td id={`ID${index}`} className="px-[18px] py-2 text-sm text-start font-normal text-[#031624] rounded-tl-[2px] rounded-bl-[2px]">
                {partner.user_id || '-'}
            </td>
            <td id={`Username${index}`} className="px-[18px] py-2 text-sm text-start font-normal text-[#031624]">
                {partner.user_name || '-'}
            </td>
            <td id={`Name${index}`} className="px-[18px] py-2 text-sm text-start font-normal text-[#031624]">
                {partner.last_name && partner.first_name 
                    ? `${partner.last_name}, ${partner.first_name}` 
                    : partner.first_name || partner.last_name || '-'}
            </td>
            <td id={`Email_Address${index}`} className="px-[18px] py-2 text-sm text-start font-normal text-[#031624]">
                {partner.email_address || '-'}
            </td>
            <td id={`Mobile_Number${index}`} className="px-[18px] py-2 text-sm text-start font-normal text-[#031624]">
                {partner.mobile_number && partner.mobile_number !== "" 
                    ? `${partner.mobile_country_code || ''} ${formatMobileNumber(partner.mobile_number)}` 
                    : '-'}
            </td>
            <td id={`Main_Number${index}`} className="px-[18px] py-2 text-sm text-start font-normal text-[#031624]">
                {partner.main_number 
                    ? `${partner.main_country_code || ''} ${formatMobileNumber(partner.main_number)}` 
                    : '-'}
            </td>
            <td className="px-[18px] py-2 text-sm text-start font-normal text-[#031624]">
                <ToggleSwitch
                    id={`carepartner-${partner.user_id}-sms-${index}`}
                    checked={getToggleValue('sms_alert')}
                    onChange={(value) => handleToggle('sms_alert', value)}
                    disabled={updatingField === `${partner.user_id}-sms_alert`}
                    dataTestId={`carepartner-${partner.user_id}-sms-toggle`}
                />
            </td>
            <td className="px-[18px] py-2 text-sm text-start font-normal text-[#031624]">
                <ToggleSwitch
                    id={`carepartner-${partner.user_id}-email-${index}`}
                    checked={getToggleValue('email_alert')}
                    onChange={(value) => handleToggle('email_alert', value)}
                    disabled={updatingField === `${partner.user_id}-email_alert`}
                    dataTestId={`carepartner-${partner.user_id}-email-toggle`}
                />
            </td>
            <td className="px-[18px] py-2 text-sm text-start font-normal text-[#031624]">
                <ToggleSwitch
                    id={`carepartner-${partner.user_id}-missed-${index}`}
                    checked={getToggleValue('missed_reading_alert')}
                    onChange={(value) => handleToggle('missed_reading_alert', value)}
                    disabled={updatingField === `${partner.user_id}-missed_reading_alert`}
                    dataTestId={`carepartner-${partner.user_id}-missed-reading-toggle`}
                />
            </td>
            <td className="px-[18px] py-2 text-sm text-start font-normal text-[#031624] rounded-tr-[2px] rounded-br-[2px]">
                <ToggleSwitch
                    id={`carepartner-${partner.user_id}-received-${index}`}
                    checked={getToggleValue('reading_received_alert')}
                    onChange={(value) => handleToggle('reading_received_alert', value)}
                    disabled={updatingField === `${partner.user_id}-reading_received_alert`}
                    dataTestId={`carepartner-${partner.user_id}-reading-received-toggle`}
                />
            </td>
        </tr>
    );
}, (prevProps, nextProps) => {
    // Only re-render if partner data or server data changed
    return (
        prevProps.partner.user_id === nextProps.partner.user_id &&
        prevProps.partner.user_name === nextProps.partner.user_name &&
        prevProps.partner.first_name === nextProps.partner.first_name &&
        prevProps.partner.last_name === nextProps.partner.last_name &&
        prevProps.partner.email_address === nextProps.partner.email_address &&
        prevProps.partner.mobile_number === nextProps.partner.mobile_number &&
        prevProps.partner.main_number === nextProps.partner.main_number &&
        prevProps.serverPartnerData?.sms_alert === nextProps.serverPartnerData?.sms_alert &&
        prevProps.serverPartnerData?.email_alert === nextProps.serverPartnerData?.email_alert &&
        prevProps.serverPartnerData?.missed_reading_alert === nextProps.serverPartnerData?.missed_reading_alert &&
        prevProps.serverPartnerData?.reading_received_alert === nextProps.serverPartnerData?.reading_received_alert &&
        prevProps.index === nextProps.index
    );
});

CarePartnerRow.displayName = 'CarePartnerRow';

export default function CarePartnersTable() {
    const { isSideBar, setToastmessage, setToastErrorMessage } = useContext(GlobalContext);
    const { data, isFetching, isLoading, status } = useGetAlertPreferencesQuery('care-partners', { refetchOnMountOrArgChange: true });
    const [updateCarePartnerAlertPreferences] = useUpdateCarePartnerAlertPreferencesMutation();

    // Handle API response structure - backend now returns only care_partners when type=care-partners
    // Could be array directly, wrapped in data property, or in care_partners property
    const responseData = data?.data || data;
    const carePartners = Array.isArray(responseData) 
        ? responseData 
        : (responseData?.care_partners || []);

    return (
        <>
            <div className='flex items-center my-3.5'>
                <h1 className='text-lg font-normal text-[#000000] min-w-max'>My Care Partners</h1>
                <div className='relative w-full h-[1px] bg-[#008586] ml-3 down-arraow'></div>
            </div>
            <div className='border border-[#EDD7E1] mt-4 rounded'>
                <div className='relative flex border-b border-[#EDD7E1] justify-between items-center py-3.5 pr-4 pl-[18px]'>
                    <p className='text-base font-semibold text-[#972E4C]' id="carepartner-list">Care Partners List</p>
                </div>
                {carePartners.length === 0
                    ? <Emptyscreenwithouttitle customHeight={false} FirstPTag={"No care partners linked yet"} />
                    :
                    <div className='overflow-auto scrollbar_purple pb-2'>
                        {((isFetching && status !== "fulfilled" && status !== "rejected") || (isLoading) || (data === undefined && status !== "fulfilled" && status !== "rejected")) 
                            ? <CustomOverLay 
                                Active 
                                text="Loading Care Partner List..." 
                                classKey={`top-0 left-0 right-0 z-50 overflow-hidden bg-gray-400 opacity-75 flex flex-col items-center justify-center w-auto ${isSideBar ? 'opened_sidebar_overlay2' : 'overlay-list2'}`} 
                            /> 
                            : <>
                                <table aria-label="Care Partners Table" className="w-full table-auto">
                                    <thead>
                                        <tr className="h-[38px] w-full bg-[#EDD7E1] rounded-sm sticky top-[0px] left-0 z-[1]">
                                            <th className="px-[18px] py-2 rounded-tl-sm rounded-bl-sm min-w-[100px] text-start">
                                                <label htmlFor="top-id" className="text-sm font-normal text-[#031624] w-full cursor-pointer">ID</label>
                                            </th>
                                            <th id="last" className="px-[18px] py-2 min-w-[140px] rounded-tl-sm rounded-bl-sm">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Username</span>
                                            </th>
                                            <th id="status" className="px-[18px] py-2 min-w-[180px]">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Name</span>
                                            </th>
                                            <th className="px-[18px] py-2 min-w-[150px]">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Email Address</span>
                                            </th>
                                            <th className="px-[18px] py-2 min-w-[150px]">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Mobile Number</span>
                                            </th>
                                            <th className="px-[18px] py-2 min-w-[150px]">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Main Number</span>
                                            </th>
                                            <th className="px-[18px] py-2 min-w-[150px]">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Alerts by SMS</span>
                                            </th>
                                            <th className="px-[18px] py-2 min-w-[150px]">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Alerts by Email</span>
                                            </th>
                                            <th className="px-[18px] py-2 min-w-[200px]">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Include Missed Readings</span>
                                            </th>
                                            <th className="px-[18px] py-2 min-w-[280px]">
                                                <span className="flex items-center justify-start text-sm font-normal text-[#000000]">Receive Readings Acknowledgement</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {carePartners.map((partner, index) => (
                                            <CarePartnerRow
                                                key={partner.user_id || index}
                                                partner={partner}
                                                index={index}
                                                updateCarePartnerAlertPreferences={updateCarePartnerAlertPreferences}
                                                setToastmessage={setToastmessage}
                                                setToastErrorMessage={setToastErrorMessage}
                                                serverPartnerData={partner}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        }
                    </div>
                }
            </div>
        </>
    );
}

